import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { CaretLeft, CaretRight, CheckCircle, Circle, CaretDown } from "@phosphor-icons/react";
import RuleNote from "./RuleNote";

const RAG = { green: "#00A86B", amber: "#FFB020", red: "#E63946", acknowledged: "#4b5563" };
const JUR = { IE: { c: "#00A86B", label: "IE" }, UK: { c: "#5B8DEF", label: "UK" }, US: { c: "#C77DFF", label: "US" } };
const JurPill = ({ j }) => { const x = JUR[j] || JUR.IE; return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${x.c}22`, color: x.c }}>{x.label}</span>; };
const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function CalendarTab() {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [events, setEvents] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => { const r = await api.get("/compliance/calendar"); setEvents(r.data.events || []); }, []);
  useEffect(() => { load(); }, [load]);

  const toggleStep = async (ev, step, done) => {
    setEvents((prev) => prev.map((e) => {
      if (e.event_key !== ev.event_key) return e;
      const checklist = e.checklist.map((s) => s.id === step ? { ...s, done } : s);
      return { ...e, checklist, checklist_done: checklist.filter((s) => s.done).length };
    }));
    try { await api.post("/compliance/checklist/toggle", { event_key: ev.event_key, step, done }); }
    catch { load(); }
  };

  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [events]);

  const monthKey = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`;
  const monthEvents = events.filter((e) => e.date.startsWith(monthKey)).sort((a, b) => a.date.localeCompare(b.date));

  const first = new Date(cursor.y, cursor.m, 1);
  const startOffset = (first.getDay() + 6) % 7; // Mon-start
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const shift = (delta) => setCursor((c) => {
    const nm = c.m + delta;
    return { y: c.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
  });
  const todayStr = new Date().toISOString().slice(0, 10);
  const jumpTo = (ds) => { const [y, m] = ds.split("-"); setCursor({ y: Number(y), m: Number(m) - 1 }); };
  const upcoming = events
    .filter((e) => !e.acknowledged)
    .sort((a, b) => a.date.localeCompare(b.date))
    .filter((e) => e.days_remaining >= -60)
    .slice(0, 5);

  return (
    <div className="space-y-6" data-testid="calendar-tab">
      {upcoming.length > 0 && (
        <div className="glass rounded-2xl p-5" data-testid="cal-upcoming">
          <div className="eyebrow mb-3">Next deadlines</div>
          <div className="space-y-2">
            {upcoming.map((e) => (
              <button key={`${e.type}-${e.date}`} onClick={() => jumpTo(e.date)} className="w-full flex items-center justify-between rounded-xl bg-black/25 border border-white/5 px-4 py-3 hover:border-white/15 transition-colors text-left" data-testid="cal-upcoming-item">
                <div className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: RAG[e.status] }} />
                  <div>
                    <div className="text-sm font-semibold">{e.title}</div>
                    <div className="text-xs text-white/50 mono flex items-center gap-2"><JurPill j={e.jurisdiction} /> {e.date} · {e.type}</div>
                  </div>
                </div>
                <span className="text-xs font-semibold" style={{ color: RAG[e.status] }}>
                  {e.days_remaining < 0 ? `${Math.abs(e.days_remaining)}d overdue` : e.days_remaining === 0 ? "Due today" : `${e.days_remaining}d left`}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="font-display text-2xl">{MONTHS[cursor.m]} {cursor.y}</div>
        <div className="flex gap-2">
          <button onClick={() => shift(-1)} data-testid="cal-prev" className="btn-ghost px-3"><CaretLeft size={16} /></button>
          <button onClick={() => setCursor({ y: now.getFullYear(), m: now.getMonth() })} className="btn-ghost text-sm">Today</button>
          <button onClick={() => shift(1)} data-testid="cal-next" className="btn-ghost px-3"><CaretRight size={16} /></button>
        </div>
      </div>

      <div className="glass rounded-2xl p-4">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {WD.map((w) => <div key={w} className="text-center text-[10px] uppercase tracking-wider text-white/40 py-1">{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const ds = `${monthKey}-${String(d).padStart(2, "0")}`;
            const evs = byDate[ds] || [];
            const isToday = ds === todayStr;
            return (
              <div key={i} className={`min-h-[68px] rounded-lg border p-1.5 ${isToday ? "border-[#FFB020] bg-[#FFB020]/5" : "border-white/5 bg-black/20"}`} data-testid={evs.length ? `cal-day-${ds}` : undefined}>
                <div className={`text-xs ${isToday ? "text-[#FFB020] font-bold" : "text-white/50"}`}>{d}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {evs.map((e) => (
                    <span key={`${e.type}-${e.date}`} title={`${e.title} (${e.type})`} className="text-[8px] font-bold px-1 rounded" style={{ background: `${RAG[e.status]}22`, color: RAG[e.status] }}>{e.type}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="eyebrow mb-3">Deadlines this month</div>
        {monthEvents.length === 0 ? (
          <div className="glass rounded-xl p-6 text-center text-white/40 text-sm">No statutory deadlines this month.</div>
        ) : (
          <div className="space-y-2">
            {monthEvents.map((e) => {
              const isOpen = expanded === e.event_key;
              const total = e.checklist_total || 0;
              const done = e.checklist_done || 0;
              const allDone = total > 0 && done === total;
              return (
                <div key={`${e.type}-${e.date}`} className="glass rounded-xl overflow-hidden" data-testid="cal-agenda-item">
                  <button type="button" onClick={() => setExpanded(isOpen ? null : e.event_key)}
                    className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/3 transition-colors" data-testid={`cal-agenda-toggle-${e.event_key}`}>
                    <div className="flex items-center gap-3">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: RAG[e.status] }} />
                      <div>
                        <div className="text-sm font-semibold">{e.title}</div>
                        <div className="text-xs text-white/50 mono flex items-center gap-2"><JurPill j={e.jurisdiction} /> {e.date} · {e.type}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {total > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid={`cal-checklist-progress-${e.event_key}`}
                          style={{ background: allDone ? "#00A86B22" : "#ffffff10", color: allDone ? "#00A86B" : "#9FB0AA" }}>
                          {allDone ? "Filed ✓" : `${done}/${total}`}
                        </span>
                      )}
                      <span className="text-xs font-semibold" style={{ color: RAG[e.status] }}>
                        {e.acknowledged ? "Filed" : e.days_remaining < 0 ? `${Math.abs(e.days_remaining)}d overdue` : `${e.days_remaining}d left`}
                      </span>
                      <CaretDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {isOpen && total > 0 && (
                    <div className="px-4 pb-4 border-t border-white/5" data-testid={`cal-checklist-${e.event_key}`}>
                      <div className="text-[10px] uppercase tracking-wider text-white/40 mt-3 mb-2">Filing checklist</div>
                      <div className="space-y-1.5">
                        {e.checklist.map((s) => (
                          <button type="button" key={s.id} onClick={() => toggleStep(e, s.id, !s.done)}
                            className="w-full flex items-center gap-2.5 text-left py-1" data-testid={`checklist-step-${e.event_key}-${s.id}`}>
                            {s.done ? <CheckCircle size={18} weight="fill" color="#00A86B" /> : <Circle size={18} color="#8A9A94" />}
                            <span className={`text-sm ${s.done ? "text-white/40 line-through" : "text-white/80"}`}>{s.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <RuleNote id="deadlines" />
    </div>
  );
}
